"use client";

import { useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  label: "All",
  slug: "all"
}, {
  label: "Risk Analysis",
  slug: "risk-analysis"
}, {
  label: "Compliance",
  slug: "compliance"
}, {
  label: "Case Studies",
  slug: "case-studies"
}, {
  label: "Technical Advisories",
  slug: "advisories"
}];
function formatDate(dateString?: string): string {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}
function estimateReadingTime(content?: string, excerpt?: string): string {
  const text = (content || excerpt || "").replace(/<[^>]+>/g, " ");
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 220));
  return `${minutes} min read`;
}
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").trim();
}
function BriefingsFeed() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="mt-16 space-y-12">
        <div className="h-[440px] bg-card/40 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[420px] bg-card/40 animate-pulse" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="mt-16 text-center text-muted-foreground">
        Unable to load technical dossiers at this time.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="mt-16 text-center text-muted-foreground">
        No technical reports published yet. Check back soon.
      </div>;
  }
  const featured = posts[0];
  const rest = posts.slice(1);
  return <div className="mt-16 md:mt-20">
      {}
      <article data-index={0} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pb-16 border-b border-border">
        <Link to={featured.link || "#"} className="lg:col-span-6 block overflow-hidden bg-card">
          <div className="relative aspect-[16/10] overflow-hidden">
            {featured.featuredImage ? <img src={featured.featuredImage} alt={featured.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out hover:scale-[1.03]" /> : <div className="absolute inset-0 w-full h-full bg-card" />}
          </div>
        </Link>

        <div className="lg:col-span-6 flex flex-col justify-center">
          {featured.categories && featured.categories.length > 0 && <span className="inline-flex items-center w-fit border border-border px-2.5 py-1 text-xs font-bold tracking-[0.15em] text-primary mb-5">
              {featured.categories[0].name}
            </span>}

          <h3 className="font-serif font-semibold text-2xl md:text-3xl lg:text-[2rem] leading-[1.18] tracking-[-0.012em] text-foreground">
            <Link to={featured.link || "#"} className="hover:text-primary transition-colors duration-200">
              {stripHtml(featured.title)}
            </Link>
          </h3>

          {featured.excerpt && <p className="mt-5 text-base md:text-lg leading-[1.6] text-muted-foreground font-default max-w-prose">
              {stripHtml(featured.excerpt).slice(0, 220)}
              {stripHtml(featured.excerpt).length > 220 ? "…" : ""}
            </p>}

          <div className="mt-6 flex items-center gap-4 text-[0.8125rem] font-medium tracking-[0.01em] text-muted-foreground">
            <span className="font-mono text-xs tracking-[0.04em]">
              {formatDate(featured.date)}
            </span>
            <span className="w-1 h-1 bg-muted-foreground/50" aria-hidden="true" />
            <span>{estimateReadingTime(featured.content, featured.excerpt)}</span>
          </div>

          <div className="mt-8">
            <Link to={featured.link || "#"} className="group inline-flex items-center text-sm font-bold text-primary relative">
              <span className="relative">
                Read full report
                <span aria-hidden="true" className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-100 bg-primary transition-transform duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]" />
              </span>
              <span className="ml-2 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true">
                →
              </span>
            </Link>
          </div>
        </div>
      </article>

      {}
      {rest.length > 0 && <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {rest.map((post, i) => <article key={post.id} data-index={i} className="group bg-card border border-border overflow-hidden flex flex-col transition-colors duration-[240ms] ease-out hover:border-primary/40">
              <Link to={post.link || "#"} className="block overflow-hidden">
                <div className="relative aspect-[4/3] overflow-hidden bg-background">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" /> : <div className="absolute inset-0 w-full h-full bg-background" />}
                </div>
              </Link>

              <div className="flex flex-col flex-1 p-6 md:p-7">
                {post.categories && post.categories.length > 0 && <span className="inline-flex items-center w-fit border border-border px-2.5 py-1 text-xs font-bold tracking-[0.15em] text-primary mb-4">
                    {post.categories[0].name}
                  </span>}

                <h3 className="font-serif font-semibold text-xl md:text-[1.375rem] leading-[1.18] tracking-[-0.012em] text-foreground">
                  <Link to={post.link || "#"} className="hover:text-primary transition-colors duration-200">
                    {stripHtml(post.title)}
                  </Link>
                </h3>

                {post.excerpt && <p className="mt-3 text-base leading-[1.65] text-muted-foreground font-default line-clamp-2">
                    {stripHtml(post.excerpt)}
                  </p>}

                <div className="mt-6 pt-5 border-t border-border flex items-center gap-3 text-[0.8125rem] font-medium tracking-[0.01em] text-muted-foreground">
                  <span className="font-mono text-xs tracking-[0.04em]">
                    {formatDate(post.date)}
                  </span>
                  <span className="w-1 h-1 bg-muted-foreground/50" aria-hidden="true" />
                  <span>{estimateReadingTime(post.content, post.excerpt)}</span>
                </div>
              </div>
            </article>)}
        </div>}

      {}
      {(hasNext || hasPrev) && <div className="mt-20 flex items-center justify-center gap-4">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="border-border text-foreground hover:bg-primary/5 disabled:opacity-40">
            ← Previous
          </Button>
          <span className="text-sm text-muted-foreground font-mono tracking-[0.04em]">
            {currentPage} / {totalPages || 1}
          </span>
          <Button variant="outline" onClick={nextPage} disabled={!hasNext} className="border-border text-foreground hover:bg-primary/5 disabled:opacity-40">
            Next →
          </Button>
        </div>}
    </div>;
}
export default function Insights() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const wp_query_params: Record<string, any> = {
    post_type: "post",
    per_page: 7,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="15881" id="technical-insights" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 lg:px-16">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          {}
          <div className="lg:col-span-8">
            <div className="h-px w-16 bg-primary" aria-hidden="true" />
            <div className="mt-3">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-primary font-default">
                Dossier
              </span>
            </div>
            <h2 className="mt-5 font-serif font-semibold text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08] tracking-[-0.018em] text-foreground">
              Engineering <em className="not-italic italic text-primary font-serif">resilience</em> through technical rigor.
            </h2>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-[1.6] text-muted-foreground font-default">
              Fire protection engineering and risk management for hazardous industries. Our insights provide the technical clarity required for industrial safety and insurance underwriting.
            </p>
          </div>

          {}
          <div className="lg:col-span-4">
            <nav aria-label="Briefing categories" className="flex flex-wrap gap-x-6 gap-y-3 lg:justify-end">
              {categories.map((cat, i) => {
              const isActive = activeCategory === cat.slug;
              return <Button key={cat.slug} data-index={i} variant="ghost" onClick={() => setActiveCategory(cat.slug)} className="relative h-auto p-0 text-sm font-bold text-foreground font-default hover:bg-transparent transition-colors duration-200" aria-pressed={isActive}>
                    <span className="relative inline-block pb-1">
                      {cat.label}
                      <span aria-hidden="true" className={"absolute left-0 bottom-0 h-px w-full bg-primary origin-left transition-transform duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)] " + (isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100")} />
                    </span>
                  </Button>;
            })}
            </nav>
          </div>
        </div>

        {}
        <PostsProvider wp_query={wp_query}>
          <BriefingsFeed />
        </PostsProvider>
      </div>
    </section>;
}